import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, X, Download, AlertCircle, Edit3, Trash2, CheckCircle2,
} from "lucide-react";
import { api, API } from "../../lib/api";

/**
 * V25 Phase 5 — Admin Attendance Viewer.
 * Shows full attendance for an ongoing tuition with:
 *  - Monthly summary chips
 *  - Per-record override (admin can edit any field + push to edit_history)
 *  - Per-record delete
 *  - CSV export
 */
const STATUS_TONE = {
    present: "bg-emerald-100 text-emerald-700 border-emerald-300",
    absent: "bg-rose-100 text-rose-700 border-rose-300",
    cancelled: "bg-slate-100 text-slate-700 border-slate-300",
    makeup: "bg-blue-100 text-blue-700 border-blue-300",
    pending: "bg-amber-100 text-amber-700 border-amber-300",
};

export default function AdminAttendanceViewer({ ongoingId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [month, setMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/ongoing-tuitions/${ongoingId}/attendance?month=${month}`);
            setData(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [ongoingId, month]);

    const removeRecord = async (recordId) => {
        if (!window.confirm("Delete this attendance record?")) return;
        try {
            await api.delete(`/admin/ongoing-tuitions/${ongoingId}/attendance/${recordId}`);
            toast.success("Deleted");
            await load();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    };

    const exportCsv = () => {
        const token = localStorage.getItem("at_token");
        const url = `${API}/admin/ongoing-tuitions/${ongoingId}/attendance.csv`;
        // Use fetch to inject auth, then download blob
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `attendance_${ongoingId.slice(0, 8)}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
            });
    };

    const records = data?.records || [];
    const summary = data?.summary || {};

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" data-testid="admin-attendance-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white p-4 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Attendance</div>
                        <h2 className="font-bold text-lg leading-tight">{data?.student_name || "—"}</h2>
                        <div className="text-xs opacity-90">Tutor: {data?.tutor_name} · {data?.schedule}</div>
                    </div>
                    <button onClick={onClose} data-testid="admin-attendance-close" className="text-white/70 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                        data-testid="admin-attendance-month"
                        className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
                    {[
                        { k: "total", l: "Total", tone: "bg-slate-100 text-slate-700" },
                        { k: "present", l: "Present", tone: "bg-emerald-50 text-emerald-700" },
                        { k: "absent", l: "Absent", tone: "bg-rose-50 text-rose-700" },
                        { k: "pct", l: "%", tone: "bg-violet-50 text-violet-700" },
                    ].map((s) => (
                        <div key={s.k} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${s.tone}`}>
                            {s.l}: <span className="font-extrabold">{summary[s.k] ?? (s.k === "pct" ? "—" : 0)}{s.k === "pct" && summary.pct != null ? "%" : ""}</span>
                        </div>
                    ))}
                    <button onClick={exportCsv} data-testid="admin-attendance-csv"
                        className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg">
                        <Download className="w-3.5 h-3.5" /> CSV
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-3">
                    {loading ? (
                        <div className="text-center py-10 text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-1" /> Loading…</div>
                    ) : records.length === 0 ? (
                        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 text-center text-slate-500 text-sm" data-testid="admin-attendance-empty">
                            <AlertCircle className="w-6 h-6 mx-auto text-slate-300 mb-1" />
                            No attendance records for this month.
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-3 sm:mx-0">
                        <table className="w-full text-sm min-w-[520px]" data-testid="admin-attendance-table">
                            <thead className="text-xs text-slate-500 border-b border-slate-200">
                                <tr>
                                    <th className="text-left py-2">Date</th>
                                    <th className="text-left py-2">Status</th>
                                    <th className="text-left py-2">Topics / Homework</th>
                                    <th className="text-left py-2">Marked by</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((r) => {
                                    const tone = STATUS_TONE[r.status] || STATUS_TONE.pending;
                                    return (
                                        <tr key={r.id} className="border-b border-slate-100" data-testid={`admin-attendance-row-${r.id}`}>
                                            <td className="py-2 align-top">
                                                <div className="font-semibold text-slate-800">{r.date}</div>
                                                <div className="text-[10px] text-slate-500">{r.time_slot}</div>
                                            </td>
                                            <td className="py-2 align-top">
                                                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${tone}`}>{r.status}</span>
                                                {r.edited_by_admin && <div className="text-[9px] text-amber-700 mt-1">ADMIN OVERRIDE</div>}
                                            </td>
                                            <td className="py-2 align-top text-xs text-slate-600 max-w-[200px]">
                                                {r.topics_covered && <div className="truncate"><strong>T:</strong> {r.topics_covered}</div>}
                                                {r.homework_assigned && <div className="truncate"><strong>HW:</strong> {r.homework_assigned}</div>}
                                            </td>
                                            <td className="py-2 align-top text-[11px] text-slate-500">{r.marked_by}</td>
                                            <td className="py-2 align-top">
                                                <div className="flex gap-1">
                                                    <button onClick={() => setEditing(r)} data-testid={`admin-attendance-edit-${r.id}`}
                                                        className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><Edit3 className="w-3.5 h-3.5" /></button>
                                                    <button onClick={() => removeRecord(r.id)} data-testid={`admin-attendance-delete-${r.id}`}
                                                        className="p-1.5 hover:bg-rose-50 rounded text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>

                {editing && (
                    <OverrideModal ongoingId={ongoingId} record={editing}
                        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
                )}
            </div>
        </div>
    );
}

function OverrideModal({ ongoingId, record, onClose, onSaved }) {
    const [form, setForm] = useState({
        date: record.date,
        status: record.status === "pending" ? "present" : record.status,
        time_slot: record.time_slot || "",
        duration_minutes: record.duration_minutes || 60,
        topics_covered: record.topics_covered || "",
        homework_assigned: record.homework_assigned || "",
        homework_status: record.homework_status || "",
        notes_tutor: record.notes_tutor || "",
        notes_admin: record.notes_admin || "",
        override_reason: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            await api.patch(`/admin/ongoing-tuitions/${ongoingId}/attendance/${record.id}`, {
                ...form,
                duration_minutes: parseInt(form.duration_minutes, 10) || null,
            });
            toast.success("Override saved");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally { setBusy(false); }
    };

    const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm";

    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center px-4" data-testid="admin-attendance-edit-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="bg-amber-500 text-white p-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Admin override</div>
                        <h3 className="font-bold text-base">Edit attendance · {record.date}</h3>
                    </div>
                    <button onClick={onClose}><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
                    <label className="block"><span className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1 block">Status</span>
                        <div className="grid grid-cols-4 gap-2">
                            {["present", "absent", "cancelled", "makeup"].map((s) => (
                                <button key={s} onClick={() => setForm({ ...form, status: s })}
                                    data-testid={`admin-attendance-override-status-${s}`}
                                    className={`px-2 py-2 rounded-lg text-xs font-bold uppercase border-2 ${form.status === s ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-500"}`}>
                                    {s}
                                </button>
                            ))}
                        </div>
                    </label>
                    <label className="block"><span className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1 block">Topics</span>
                        <textarea value={form.topics_covered} onChange={(e) => setForm({ ...form, topics_covered: e.target.value })} rows={2} className={inputCls} data-testid="admin-attendance-override-topics" />
                    </label>
                    <label className="block"><span className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1 block">Admin notes (private)</span>
                        <textarea value={form.notes_admin} onChange={(e) => setForm({ ...form, notes_admin: e.target.value })} rows={2} className={inputCls} data-testid="admin-attendance-override-admin-notes" />
                    </label>
                    <label className="block"><span className="text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1 block">Override reason</span>
                        <input value={form.override_reason} onChange={(e) => setForm({ ...form, override_reason: e.target.value })} className={inputCls} data-testid="admin-attendance-override-reason" placeholder="Why are you overriding?" />
                    </label>
                    <button onClick={submit} disabled={busy} data-testid="admin-attendance-override-save"
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save override
                    </button>
                </div>
            </div>
        </div>
    );
}
